import React, { useState, useEffect, useRef } from 'react';
import {
  Users,
  MessageSquare,
  Lightbulb,
  Plus,
  Send,
  MoreVertical,
  Settings,
  UserPlus,
  Search,
  Filter,
  Pin,
  ThumbsUp,
  Edit2,
  Trash2,
  Download,
  Image,
  Info,
  Paperclip,
  X,
  Check,
  ChevronDown,
  Loader2,
  LogOut,
  TrendingUp,
  BarChart3,
  Target,
  AlertCircle,
} from 'lucide-react';
import cloudService from '../services/cloudService';
import collaborationService from '../services/collaborationService';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const CollaborationRoom = () => {
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [insights, setInsights] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [inviteSearch, setInviteSearch] = useState('');
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [activeTab, setActiveTab] = useState('messages');
  const [attachedFile, setAttachedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const [roomSearchQuery, setRoomSearchQuery] = useState('');
  const [showEditRoomModal, setShowEditRoomModal] = useState(false);
  const [editRoomForm, setEditRoomForm] = useState({ name: '', description: '' });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // API calls
  const fetchMyRooms = async () => {
    try {
      const data = await collaborationService.getMyRooms();
      setRooms(data.rooms || []);
      if (data.rooms && data.rooms.length > 0 && !selectedRoom) {
        setSelectedRoom(data.rooms[0]);
      }
    } catch (error) {
      console.log('API endpoint not available, using empty state');
      // If API fails, show empty state so user can create rooms
      setRooms([]);
    }
  };

  const fetchPendingInvitations = async () => {
    try {
      const data = await collaborationService.getPendingInvitations();
      console.log('Pending invitations response:', data);
      setPendingInvitations(data.rooms || []);
    } catch (error) {
      console.log('Failed to fetch pending invitations:', error);
    }
  };

  const fetchConnectedUsers = async () => {
    const currentUserId = localStorage.getItem('userId');
    if (!currentUserId) return;
    try {
      const data = await collaborationService.getConnections(currentUserId);
      const list = data.connections || data.content || [];
      const active = list.filter((c) => c.status === 'ACTIVE' || c.status === 'active');
      const extracted = active.map((c) => {
        const isRequester = c.requesterId === currentUserId;
        return {
          userId: isRequester ? c.recipientId : c.requesterId,
          fullName: isRequester ? c.recipientFullName : c.requesterFullName,
          role: isRequester ? c.recipientRole : c.requesterRole,
        };
      });
      setConnectedUsers(extracted);
    } catch (error) {
      console.log('Failed to fetch connected users');
    }
  };

  const handleAcceptInvitation = async (roomId) => {
    try {
      await collaborationService.acceptInvitation(roomId);
      setPendingInvitations((prev) => prev.filter((r) => r.roomId !== roomId));
      fetchMyRooms();
    } catch (error) {
      console.log('Failed to accept invitation');
    }
  };

  const handleUpdateRoom = async () => {
    if (!editRoomForm.name.trim()) return;
    try {
      const data = await collaborationService.updateRoom(selectedRoom.roomId, {
        name: editRoomForm.name.trim(),
        description: editRoomForm.description.trim(),
      });
      setSelectedRoom(data);
      setRooms((prev) => prev.map((r) => r.roomId === data.roomId ? data : r));
      setShowEditRoomModal(false);
    } catch (error) {
      console.log('Failed to update room', error);
    }
  };

  const handleLeaveRoom = async () => {
    if (!window.confirm('Are you sure you want to leave this room?')) return;
    try {
      await collaborationService.leaveRoom(selectedRoom.roomId);
      setRooms((prev) => prev.filter((r) => r.roomId !== selectedRoom.roomId));
      setSelectedRoom(null);
    } catch (error) {
      console.log('Failed to leave room', error);
    }
  };

  const handleRemoveParticipant = async (participantId, participantName) => {
    if (!window.confirm(`Are you sure you want to remove ${participantName} from this room?`)) return;
    try {
      await collaborationService.removeParticipant(selectedRoom.roomId, participantId);
      setParticipants((prev) => prev.filter((p) => p.participantId !== participantId));
    } catch (error) {
      console.log('Failed to remove participant', error);
    }
  };

  const handleDeclineInvitation = async (roomId) => {
    try {
      await collaborationService.declineInvitation(roomId);
      setPendingInvitations((prev) => prev.filter((r) => r.roomId !== roomId));
    } catch (error) {
      console.log('Failed to decline invitation');
    }
  };

  const fetchRoomDetails = async () => {
    try {
      const data = await collaborationService.getRoomDetails(selectedRoom.roomId);
      setParticipants(data.participants || []);
    } catch (error) {
      console.log('API endpoint not available');
      setParticipants([]);
    }
  };

  const fetchMessages = async () => {
    try {
      const data = await collaborationService.getMessages(selectedRoom.roomId);
      setMessages(data.messages || []);
    } catch (error) {
      console.log('API endpoint not available');
      setMessages([]);
    }
  };

  const fetchInsights = async () => {
    try {
      const data = await collaborationService.getInsights(selectedRoom.roomId);
      setInsights(data.insights || []);
    } catch (error) {
      console.log('API endpoint not available');
      setInsights([]);
    }
  };

  useEffect(() => {
    console.log('✅ CollaborationRoom (NEW VERSION) successfully mounted!');
    console.log('📦 This is the component with room creation, messaging, and insights');
    fetchMyRooms();
    fetchPendingInvitations();
    fetchConnectedUsers();
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      fetchRoomDetails();
      fetchMessages();
      fetchInsights();
    }
  }, [selectedRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const createRoom = async (roomData) => {
    try {
      const data = await collaborationService.createRoom(roomData);
      setRooms([...rooms, data]);
      setSelectedRoom(data);
      setShowCreateRoom(false);
    } catch (error) {
      console.log('API not available, creating room locally');
      // Create room locally if API not available
      const newRoom = {
        roomId: Date.now(),
        name: roomData.name,
        description: roomData.description,
        roomType: roomData.roomType,
        isPrivate: roomData.isPrivate,
        createdAt: new Date().toISOString(),
        participantCount: 1,
      };
      setRooms([...rooms, newRoom]);
      setSelectedRoom(newRoom);
      setShowCreateRoom(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && !attachedFile) return;
    setUploadingFile(true);

    let attachmentUrl = null;
    let attachmentName = null;
    let attachmentType = null;

    try {
      if (attachedFile) {
        const result = await cloudService.uploadFile(attachedFile, 'room-messages');
        attachmentUrl = result.key || result.url;
        attachmentName = attachedFile.name;
        attachmentType = attachedFile.type.startsWith('image/') ? 'IMAGE' : 'FILE';
      }

      const data = await collaborationService.postMessage(selectedRoom.roomId, {
        content: newMessage || (attachmentName || ''),
        messageType: attachmentType || 'TEXT',
        attachmentUrl,
        attachmentName,
        attachmentType,
      });

      setMessages([...messages, data]);
      setNewMessage('');
      setAttachedFile(null);
    } catch (error) {
      console.log('Failed to send message', error);
    }
    setUploadingFile(false);
  };

  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editMessageContent, setEditMessageContent] = useState('');

  const handleEditMessage = async (messageId) => {
    if (!editMessageContent.trim()) return;
    try {
      await collaborationService.updateMessage(selectedRoom.roomId, messageId, { content: editMessageContent });
      setMessages((prev) => prev.map((m) => m.messageId === messageId ? { ...m, content: editMessageContent, isEdited: true } : m));
      setEditingMessageId(null);
      setEditMessageContent('');
    } catch (error) {
      console.log('Failed to edit message', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await collaborationService.deleteMessage(selectedRoom.roomId, messageId);
      setMessages((prev) => prev.filter((m) => m.messageId !== messageId));
    } catch (error) {
      console.log('Failed to delete message', error);
    }
  };

  const postInsight = async (insightData) => {
    try {
      const data = await collaborationService.postInsight(selectedRoom.roomId, insightData);
      setInsights([data, ...insights]);
      setShowInsightModal(false);
    } catch (error) {
      console.log('API not available, adding insight locally');
      // Add insight locally if API not available
      const newInsight = {
        insightId: Date.now(),
        title: insightData.title,
        content: insightData.content,
        category: insightData.category,
        author: { firstName: 'You', lastName: '', userId: 1 },
        postedAt: new Date().toISOString(),
        isPinned: false,
        likeCount: 0,
      };
      setInsights([newInsight, ...insights]);
      setShowInsightModal(false);
    }
  };

  const inviteParticipant = async (userId, role = 'MEMBER') => {
    try {
      await collaborationService.inviteParticipantToRoom(selectedRoom.roomId, {
        userId,
        role,
        invitationMessage: `You've been invited to join ${selectedRoom.name}`,
      });
      fetchRoomDetails();
      setShowInviteModal(false);
    } catch (error) {
      console.log('API not available', error);
      setShowInviteModal(false);
    }
  };

  const likeInsight = async (insightId) => {
    try {
      await collaborationService.likeInsight(selectedRoom.roomId, insightId);
      // Update like count locally
      setInsights(insights.map(insight => 
        insight.insightId === insightId 
          ? { ...insight, likeCount: (insight.likeCount || 0) + 1 }
          : insight
      ));
    } catch (error) {
      console.log('API not available, updating like locally');
      // Update like count locally
      setInsights(insights.map(insight => 
        insight.insightId === insightId 
          ? { ...insight, likeCount: (insight.likeCount || 0) + 1 }
          : insight
      ));
    }
  };

  console.log('✅ CollaborationRoom (NEW) rendering, rooms:', rooms.length);
  console.log('📍 If you see this, the NEW component is loaded!');

  return (
    <>
      {pendingInvitations.length > 0 && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
            <UserPlus className="h-4 w-4" /> Pending Room Invitations ({pendingInvitations.length})
          </p>
          <div className="space-y-2">
            {pendingInvitations.map((inv) => (
              <div key={inv.roomId} className="flex items-center gap-4 p-3 bg-white border rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{inv.name}</p>
                  <p className="text-xs text-gray-500 truncate">{inv.description || 'No description'}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Created by: {inv.createdBy?.fullName || 'Unknown'} | Type: {inv.roomType || 'General'}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" className="h-8" onClick={() => handleAcceptInvitation(inv.roomId)}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => handleDeclineInvitation(inv.roomId)}>
                    <X className="h-3.5 w-3.5 mr-1" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex bg-gray-50 rounded-lg border" style={{ minHeight: '600px', height: 'calc(100vh - 200px)' }}>
      {/* Sidebar - Room List */}
      <div className="w-80 border-r bg-white flex flex-col overflow-hidden">
        <div className="p-4 border-b bg-white shrink-0 z-10">
          <div className="flex items-center justify-between mb-4 gap-2">
            <h2 className="text-xl font-semibold truncate">Collaboration Rooms</h2>
            <Button 
              size="sm" 
              onClick={() => {
                console.log('Create Room button clicked!');
                setShowCreateRoom(true);
              }} 
              className="shrink-0 whitespace-nowrap"
            >
              <Plus className="h-4 w-4 mr-1" />
              New
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input className="pl-10" placeholder="Search rooms..." value={roomSearchQuery} onChange={(e) => setRoomSearchQuery(e.target.value)} />
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2">
            {(() => {
              const q = roomSearchQuery.trim().toLowerCase();
              const filtered = q ? rooms.filter((r) => (r.name || '').toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q)) : rooms;
              return filtered.length === 0 && pendingInvitations.length === 0
                ? <p className="text-sm text-muted-foreground text-center py-8">No rooms yet. Create one!</p>
                : filtered.map((room) => (
              <div
                key={room.roomId}
                className={`p-3 rounded-lg cursor-pointer mb-2 hover:bg-gray-50 ${
                  selectedRoom?.roomId === room.roomId ? 'bg-blue-50 border border-blue-200' : ''
                }`}
                onClick={() => setSelectedRoom(room)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{room.name}</h3>
                      {room.isPrivate && (
                        <Badge variant="outline" className="text-xs">
                          Private
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 line-clamp-1 mt-1">{room.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {room.participantCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" />
                        {room.messageCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <Lightbulb className="h-3 w-3" />
                        {room.insightCount}
                      </span>
                    </div>
                  </div>
                  <Badge className={getRoomTypeBadgeColor(room.roomType)}>{room.roomType}</Badge>
                </div>
              </div>
            ));
            })()}
          </div>
        </ScrollArea>
      </div>

      {/* Main Content */}
      {selectedRoom ? (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Room Header */}
          <div className="border-b bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-semibold">{selectedRoom.name}</h1>
                  <Badge className={getRoomTypeBadgeColor(selectedRoom.roomType)}>
                    {selectedRoom.roomType}
                  </Badge>
                </div>
                <p className="text-gray-600 mt-1">{selectedRoom.description}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowInviteModal(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite
                </Button>
                {(() => {
                  const role = selectedRoom.userRole;
                  const isAdmin = role === 'OWNER' || role === 'ADMIN';
                  return (
                    <Button variant="outline" size="sm" onClick={() => { setEditRoomForm({ name: selectedRoom.name || '', description: selectedRoom.description || '' }); setShowEditRoomModal(true); }}>
                      {isAdmin ? <Settings className="h-4 w-4" /> : <Info className="h-4 w-4" />}
                    </Button>
                  );
                })()}
                <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50" onClick={handleLeaveRoom}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Participants Preview */}
            <div className="flex items-center gap-2 mt-3">
              <div className="flex -space-x-2">
                {participants.slice(0, 5).map((participant) => (
                  <Avatar key={participant.participantId} className="border-2 border-white w-8 h-8">
                    <AvatarImage src={participant.profilePictureUrl} alt={participant.fullName} />
                    <AvatarFallback>{participant.fullName?.substring(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <span className="text-sm text-gray-600">
                {participants.length} participant{participants.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
            <div className="border-b bg-white px-4">
              <TabsList>
                <TabsTrigger value="messages" className="gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Messages
                  {selectedRoom.messageCount > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedRoom.messageCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="insights" className="gap-2">
                  <Lightbulb className="h-4 w-4" />
                  Insights
                  {selectedRoom.insightCount > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedRoom.insightCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="participants" className="gap-2">
                  <Users className="h-4 w-4" />
                  Participants
                  <Badge variant="secondary" className="ml-2">
                    {participants.length}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Messages Tab */}
            <TabsContent value="messages" className="flex-1 flex flex-col m-0 min-h-0 overflow-hidden">
              <ScrollArea className="flex-1 p-4 min-h-0">
                <div className="space-y-4">
                  {messages.map((message) => (
                    <MessageBubble key={message.messageId} message={message} onEdit={() => { setEditingMessageId(message.messageId); setEditMessageContent(message.content); }} onDelete={() => handleDeleteMessage(message.messageId)} editingMessageId={editingMessageId} editMessageContent={editMessageContent} setEditMessageContent={setEditMessageContent} setEditingMessageId={setEditingMessageId} handleEditMessage={handleEditMessage} />
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <div className="border-t bg-white p-4 space-y-2">
                {attachedFile && (
                  <div className="flex items-center gap-2 p-2 bg-muted rounded-md text-sm">
                    <Paperclip className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 truncate">{attachedFile.name}</span>
                    <button className="text-red-500 hover:text-red-700 text-xs font-medium" onClick={() => setAttachedFile(null)}>Remove</button>
                  </div>
                )}
                <div className="flex gap-2">
                  <input type="file" ref={fileInputRef} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setAttachedFile(f); }} />
                  <Button variant="outline" size="icon" disabled={uploadingFile} onClick={() => fileInputRef.current?.click()}>
                    {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                  </Button>
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !uploadingFile && sendMessage()}
                    className="flex-1"
                    disabled={uploadingFile}
                  />
                  <Button onClick={sendMessage} disabled={(!newMessage.trim() && !attachedFile) || uploadingFile}>
                    {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Insights Tab */}
            <TabsContent value="insights" className="flex-1 m-0">
              <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Shared Insights</h3>
                  <Button onClick={() => setShowInsightModal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Post Insight
                  </Button>
                </div>

                <ScrollArea className="h-[calc(100vh-300px)]">
                  <div className="grid gap-4">
                    {insights.map((insight) => (
                      <InsightCard
                        key={insight.insightId}
                        insight={insight}
                        onLike={() => likeInsight(insight.insightId)}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </TabsContent>

            {/* Participants Tab */}
            <TabsContent value="participants" className="flex-1 m-0">
              <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Participants ({participants.length})</h3>
                  <Button variant="outline" size="sm" onClick={() => setShowInviteModal(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite More
                  </Button>
                </div>

                <ScrollArea className="h-[calc(100vh-300px)]">
                  <div className="grid gap-3">
                    {participants.map((participant) => (
                      <ParticipantCard key={participant.participantId} participant={participant} currentUserId={localStorage.getItem('userId')} onRemove={handleRemoveParticipant} />
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <MessageSquare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">No room selected</h2>
            <p className="text-gray-500 mb-4">Select a room from the sidebar or create a new one</p>
            <Button onClick={() => setShowCreateRoom(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create New Room
            </Button>
          </div>
        </div>
      )}

      {/* Create Room Modal */}
      <CreateRoomModal
        open={showCreateRoom}
        onClose={() => setShowCreateRoom(false)}
        onSubmit={createRoom}
      />

      <Dialog open={showEditRoomModal} onOpenChange={setShowEditRoomModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{selectedRoom?.userRole === 'OWNER' || selectedRoom?.userRole === 'ADMIN' ? 'Edit Room' : 'Room Details'}</DialogTitle>
            <DialogDescription>Room information</DialogDescription>
          </DialogHeader>
          {(() => {
            const isAdmin = selectedRoom?.userRole === 'OWNER' || selectedRoom?.userRole === 'ADMIN';
            return (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Room Name</label>
                  {isAdmin ? (
                    <Input value={editRoomForm.name} onChange={(e) => setEditRoomForm((p) => ({ ...p, name: e.target.value }))} placeholder="Enter room name" />
                  ) : (
                    <p className="text-sm text-muted-foreground border rounded-md p-2 bg-gray-50">{selectedRoom?.name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  {isAdmin ? (
                    <Textarea value={editRoomForm.description} onChange={(e) => setEditRoomForm((p) => ({ ...p, description: e.target.value }))} placeholder="Enter room description" className="min-h-[80px]" />
                  ) : (
                    <p className="text-sm text-muted-foreground border rounded-md p-2 bg-gray-50">{selectedRoom?.description || 'No description'}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Room Type</label>
                  <p className="text-sm text-muted-foreground border rounded-md p-2 bg-gray-50">{selectedRoom?.roomType || 'General'}</p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Participants</label>
                  <p className="text-sm text-muted-foreground border rounded-md p-2 bg-gray-50">{participants.length} participant{participants.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRoomModal(false)}>Close</Button>
            {(selectedRoom?.userRole === 'OWNER' || selectedRoom?.userRole === 'ADMIN') && <Button onClick={handleUpdateRoom} disabled={!editRoomForm.name.trim()}>Save</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Participant Modal */}
      <InviteParticipantModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSubmit={inviteParticipant}
        connectedUsers={connectedUsers}
        participantIds={participants.map((p) => p.userId)}
      />

      {/* Post Insight Modal */}
      <PostInsightModal
        open={showInsightModal}
        onClose={() => setShowInsightModal(false)}
        onSubmit={postInsight}
      />
    </div>
    </>
  );
};

const AttachmentView = ({ attachmentUrl, attachmentName }) => {
  const [presignedUrl, setPresignedUrl] = useState(null);
  useEffect(() => {
    if (!attachmentUrl) return;
    let mounted = true;
    cloudService.getDownloadPresignedUrl(attachmentUrl)
      .then((res) => { if (mounted) setPresignedUrl(res.presignedUrl || res.url || res); })
      .catch(() => { if (mounted) setPresignedUrl(null); });
    return () => { mounted = false; };
  }, [attachmentUrl]);
  if (!presignedUrl) return null;
  return (
    <a href={presignedUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded bg-white/10 hover:bg-white/20 transition-colors mt-2">
      <Paperclip className="h-4 w-4 flex-shrink-0" />
      <span className="text-sm truncate">{attachmentName || 'Attachment'}</span>
    </a>
  );
};

// Message Bubble Component
const MessageBubble = ({ message, onEdit, onDelete, editingMessageId, editMessageContent, setEditMessageContent, handleEditMessage, setEditingMessageId }) => {
  const isSystem = message.messageType === 'SYSTEM';
  const currentUserId = localStorage.getItem('userId');
  const isOwnMessage = message.user?.userId === currentUserId;
  const isEditing = editingMessageId === message.messageId;

  if (isSystem) {
    return (
      <div className="flex justify-center">
        <div className="bg-gray-100 text-gray-600 text-sm px-4 py-2 rounded-full">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex gap-3 ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
      <Avatar className="w-10 h-10">
        <AvatarImage src={message.user?.profilePictureUrl} alt={message.user?.fullName} />
        <AvatarFallback>{message.user?.fullName?.substring(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className={`flex-1 ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm">{message.user?.fullName}</span>
          <span className="text-xs text-gray-400">{formatTime(message.createdAt)}</span>
          {message.isEdited && <span className="text-xs text-gray-400">(edited)</span>}
          {isOwnMessage && !isEditing && (
            <div className="flex gap-1 ml-2">
              <button onClick={onEdit} className="text-gray-400 hover:text-blue-500 transition-colors"><Edit2 className="h-3.5 w-3.5" /></button>
              <button onClick={onDelete} className="text-gray-400 hover:text-red-500 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>
        {isEditing ? (
          <div className="flex gap-2 w-full max-w-lg">
            <Input value={editMessageContent} onChange={(e) => setEditMessageContent(e.target.value)} className="flex-1 text-sm" autoFocus />
            <Button size="sm" className="h-8" onClick={() => handleEditMessage(message.messageId)}><Check className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => setEditingMessageId(null)}><X className="h-3.5 w-3.5" /></Button>
          </div>
        ) : (
          <div className={`rounded-lg px-4 py-2 max-w-lg ${isOwnMessage ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-900'}`}>
            {message.content}
            <AttachmentView attachmentUrl={message.attachmentUrl} attachmentName={message.attachmentName} />
          </div>
        )}
      </div>
    </div>
  );
};

// Insight Card Component
const InsightCard = ({ insight, onLike }) => {
  const getInsightIcon = (type) => {
    switch (type) {
      case 'MARKET':
        return <TrendingUp className="h-5 w-5" />;
      case 'RESEARCH':
        return <BarChart3 className="h-5 w-5" />;
      case 'STRATEGY':
        return <Target className="h-5 w-5" />;
      case 'PERFORMANCE':
        return <BarChart3 className="h-5 w-5" />;
      default:
        return <Lightbulb className="h-5 w-5" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${getInsightTypeColor(insight.insightType)}`}>
              {getInsightIcon(insight.insightType)}
            </div>
            <div>
              <CardTitle className="text-lg">{insight.title}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Avatar className="w-6 h-6">
                  <AvatarImage src={insight.user?.profilePictureUrl} alt={insight.user?.fullName} />
                  <AvatarFallback>{insight.user?.fullName?.substring(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="text-sm text-gray-600">{insight.user?.fullName}</span>
                <Badge variant="outline" className="text-xs">
                  {insight.insightType}
                </Badge>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="icon">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-gray-700 whitespace-pre-wrap">{insight.content}</p>

        {insight.relatedSymbols && (
          <div className="flex gap-2 mt-3">
            {insight.relatedSymbols.split(',').map((symbol) => (
              <Badge key={symbol} variant="secondary">
                {symbol.trim()}
              </Badge>
            ))}
          </div>
        )}

        {insight.attachmentUrl && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-gray-400" />
              <span className="text-sm">{insight.attachmentName}</span>
            </div>
            <Button variant="ghost" size="sm">
              <Download className="h-4 w-4" />
            </Button>
          </div>
        )}

        <Separator className="my-4" />

        <div className="flex items-center justify-between">
          <div className="flex gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onLike}
              className={insight.hasUserLiked ? 'text-blue-600' : ''}
            >
              <ThumbsUp className="h-4 w-4 mr-1" />
              {insight.likeCount}
            </Button>
            <Button variant="ghost" size="sm">
              <MessageSquare className="h-4 w-4 mr-1" />
              {insight.commentCount}
            </Button>
          </div>
          <span className="text-xs text-gray-400">{formatTime(insight.createdAt)}</span>
        </div>
      </CardContent>
    </Card>
  );
};

// Participant Card Component
const ParticipantCard = ({ participant, currentUserId, onRemove }) => {
  const canRemove = participant.userId !== currentUserId && participant.role !== 'OWNER';
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50">
      <div className="flex items-center gap-3">
        <Avatar className="w-10 h-10">
          <AvatarImage src={participant.profilePictureUrl} alt={participant.fullName} />
          <AvatarFallback>{participant.fullName?.substring(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div>
          <div className="flex items-center gap-2">
            <h4 className="font-medium">{participant.fullName}</h4>
            {participant.roomRole === 'OWNER' && <Badge className="text-[10px] h-5">Owner</Badge>}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs">{participant.roomRole}</Badge>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        {canRemove && (
          <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700"
            onClick={() => { if (window.confirm(`Remove ${participant.fullName} from this room?`)) onRemove(participant.participantId, participant.fullName); }}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Create Room Modal
const CreateRoomModal = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    roomType: 'GENERAL',
    isPrivate: false,
  });

  const handleSubmit = () => {
    onSubmit(formData);
    setFormData({ name: '', description: '', roomType: 'GENERAL', isPrivate: false });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Collaboration Room</DialogTitle>
          <DialogDescription>
            Set up a new space for team collaboration and insights sharing
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Room Name</label>
            <Input
              placeholder="e.g., Tech Stock Analysis"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <Textarea
              placeholder="What is this room about?"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Room Type</label>
            <Select
              value={formData.roomType}
              onValueChange={(value) => setFormData({ ...formData, roomType: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GENERAL">General Discussion</SelectItem>
                <SelectItem value="RESEARCH">Research & Analysis</SelectItem>
                <SelectItem value="STRATEGY">Strategy Planning</SelectItem>
                <SelectItem value="CLIENT">Client Collaboration</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isPrivate"
              checked={formData.isPrivate}
              onChange={(e) => setFormData({ ...formData, isPrivate: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="isPrivate" className="text-sm cursor-pointer">
              Make this room private (invite-only)
            </label>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.name} className="flex-1">
            Create Room
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Invite Participant Modal
const InviteParticipantModal = ({ open, onClose, onSubmit, connectedUsers = [], participantIds = [] }) => {
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [role, setRole] = useState('MEMBER');
  const [searchQuery, setSearchQuery] = useState('');

  const handleSubmit = () => {
    if (selectedUserId) {
      onSubmit(selectedUserId, role);
      setSelectedUserId(null);
      setRole('MEMBER');
      setSearchQuery('');
    }
  };

  const participantIdSet = new Set(participantIds);
  const availableUsers = connectedUsers.filter((u) => !participantIdSet.has(u.userId));

  const filteredUsers = searchQuery.trim()
    ? availableUsers.filter((u) => (u.fullName || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : availableUsers;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite Participants</DialogTitle>
          <DialogDescription>Select from your connected users who are not yet in the room</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search connected users..." className="pl-10" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
          <ScrollArea className="h-[250px] border rounded-md p-2">
            <div className="space-y-1">
              {filteredUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No users to invite</p>
              ) : filteredUsers.map((u) => {
                const isSelected = selectedUserId === u.userId;
                return (
                  <div key={u.userId} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'}`}
                    onClick={() => setSelectedUserId(isSelected ? null : u.userId)}>
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarFallback className="text-xs">{(u.fullName || '').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{u.fullName}</p>
                      {u.role && <p className="text-xs text-muted-foreground truncate">{String(u.role).replace(/_/g, ' ')}</p>}
                    </div>
                    {isSelected && <Check className="h-4 w-4 text-blue-500 flex-shrink-0" />}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <div className="space-y-2">
            <label className="text-sm font-medium">Role</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MEMBER">Member</SelectItem>
                <SelectItem value="ADMIN">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSubmit} disabled={!selectedUserId} className="flex-1">Send Invitation</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Post Insight Modal
const PostInsightModal = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'GENERAL',
    relatedSymbols: '',
  });

  const handleSubmit = () => {
    onSubmit(formData);
    setFormData({ title: '', content: '', category: 'GENERAL', relatedSymbols: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Post Insight</DialogTitle>
          <DialogDescription>
            Share your research, analysis, or market insights with the team
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Title</label>
            <Input
              placeholder="e.g., Q4 Tech Sector Outlook"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Content</label>
            <Textarea
              placeholder="Share your detailed analysis and insights..."
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={6}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MARKET">Market Analysis</SelectItem>
                  <SelectItem value="RESEARCH">Research Report</SelectItem>
                  <SelectItem value="STRATEGY">Strategy</SelectItem>
                  <SelectItem value="PERFORMANCE">Performance Review</SelectItem>
                  <SelectItem value="GENERAL">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Related Symbols</label>
              <Input
                placeholder="AAPL, MSFT, GOOGL"
                value={formData.relatedSymbols}
                onChange={(e) => setFormData({ ...formData, relatedSymbols: e.target.value })}
              />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.title || !formData.content} className="flex-1">
            Post Insight
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper functions
const getRoomTypeBadgeColor = (type) => {
  const colors = {
    GENERAL: 'bg-blue-100 text-blue-700',
    RESEARCH: 'bg-purple-100 text-purple-700',
    STRATEGY: 'bg-green-100 text-green-700',
    CLIENT: 'bg-orange-100 text-orange-700',
  };
  return colors[type] || colors.GENERAL;
};

const getInsightTypeColor = (type) => {
  const colors = {
    MARKET: 'bg-blue-100 text-blue-700',
    RESEARCH: 'bg-purple-100 text-purple-700',
    STRATEGY: 'bg-green-100 text-green-700',
    PERFORMANCE: 'bg-orange-100 text-orange-700',
  };
  return colors[type] || 'bg-gray-100 text-gray-700';
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`;
  
  return date.toLocaleDateString();
};

export default CollaborationRoom;
